import * as React from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { StaticImage } from 'gatsby-plugin-image';
import SEO from '../components/seo';
import Image from '../components/image';
import { CenteredContent, Section, SectionContent } from '../components/utility/section';
import { defaultTransition } from '../styles/animation';
import ExtendedBg from '../components/utility/extended-bg';
import ResponsiveDiv from '../components/utility/responsive-div';
import { PaddedDiv } from '../components/utility/padded-div';
import { SpacingWrapper } from '../components/utility/spacing-wrapper';

const ServicesPage = () => (
  <>
    <SEO title="Services" />
    <ServiceSection>
      <SectionContent>
        <motion.h1
          animate={{ x: 0, opacity: 1 }}
          initial={{ x: 50, opacity: 0 }}
          transition={defaultTransition}
        >
          What's your mission?
        </motion.h1>
        <motion.p
          animate={{ x: 0, opacity: 1 }}
          initial={{ x: 100, opacity: 0 }}
          transition={{
            ...defaultTransition,
            delay: 0.1,
          }}
        >
          Ours is to partner with you and continuously innovate.
        </motion.p>
      </SectionContent>
    </ServiceSection>
    <ServiceSection>
      <ServiceSectionContent>
        <ServiceContainer>
          <Image
            staticGatsbyImage={
              <StaticImage
                src="../images/services/discovery.png"
                alt="A Launchcode employee writing down notes from a discovery phase meeting"
                loading="eager"
                layout="fullWidth"
                placeholder="dominantColor"
                quality={100}
                formats={['auto']}
              />
            }
            disableAnimation
          />
        </ServiceContainer>
        <ServiceContainer layoutId="discovery-container">
          <motion.h2 layoutId="discovery-header">Discovery</motion.h2>
          <p>
            This is all about you! You have a vision for your product and the expertise in your
            industry. We are here to help you translate your vision into a digital strategy. Our
            process creates a clear understanding of your desired outcomes with a focus on the
            future state of your business.
          </p>
          <ul>
            <li>Market Research</li>
            <li>Product Vision</li>
            <li>Mind Mapping Sessions</li>
            <li>Identifying Competitive Advantages</li>
            <li>Partnership Opportunities</li>
            <li>Grant Procurement</li>
          </ul>
        </ServiceContainer>
      </ServiceSectionContent>
    </ServiceSection>
    <ServiceSection>
      <ServiceSectionContentFlipped>
        <ServiceContainer layoutId="design-container">
          <motion.h2 layoutId="design-header">Design</motion.h2>
          <p>
            Design is more than just making things look pretty. Our goal is to understand the client
            and user interactions to create an optimal user experience. Simplifying your users life
            with an enjoyable UI/UX experience is the key to user adoption. During this process we
            create different designs by taking inspiration from the real world and latest trends to
            come up with experiences your users will love.
          </p>
          <ul>
            <li>Storyboarding</li>
            <li>User Experience Design</li>
            <li>User Interface Design</li>
            <li>Wireframe and Mockups</li>
            <li>Proof of Concept</li>
          </ul>
        </ServiceContainer>
        <ServiceContainer>
          <Image
            staticGatsbyImage={
              <StaticImage
                src="../images/services/design.png"
                alt="a collage of images displaying off some of our designs"
                loading="lazy"
                layout="fullWidth"
                placeholder="dominantColor"
                quality={100}
                formats={['auto']}
              />
            }
            disableAnimation
          />
        </ServiceContainer>
      </ServiceSectionContentFlipped>
    </ServiceSection>
    <ServiceSection>
      <ServiceSectionContent>
        <ServiceContainer>
          <Image
            staticGatsbyImage={
              <StaticImage
                src="../images/services/development.png"
                alt="A Launchcode developer quietly at work developing cool stuff"
                loading="lazy"
                layout="fullWidth"
                placeholder="dominantColor"
                quality={100}
                formats={['auto']}
              />
            }
            disableAnimation
          />
        </ServiceContainer>
        <ServiceContainer layoutId="development-container">
          <motion.h2 layoutId="development-header">Development</motion.h2>
          <p>
            We love this part of the process. This is where your vision truly starts to come to
            life. Our development team is multi faceted with experience in both software and
            hardware development. Depending on your product vision we will create prototypes in
            rapid succession of agile sprints where you get a new and improved version every couple
            weeks. We are in constant communication with you allowing you to pivot at any time as
            your priorities shift. For hardware solutions, our fabrication shop consists of 3D
            printers, CNC water jet cutter, metal brake/bends, so we can create quick prototypes to
            continuously test and refine your product.
          </p>
          <ul>
            <li>Web Development</li>
            <li>iOS & Android Development</li>
            <li>Hardware Development</li>
            <li>Data Warehouse and Data Visualization</li>
            <li>IoT & Electronics</li>
            <li>Rapid Prototyping</li>
          </ul>
        </ServiceContainer>
      </ServiceSectionContent>
    </ServiceSection>
    <ServiceSection>
      <ServiceSectionContentFlipped>
        <ServiceContainer layoutId="partnership-container">
          <motion.h2 layoutId="partnership-header">Partnership</motion.h2>
          <p>
            We are here for the long run. We treat all of our clients as partners, as their success
            is directly tied to our own success. We leverage our existing support infrastructure to
            ensure user adoption and quick response to market demands. For some clients we even
            partner in joint ventures that use our productization experience to incubate and grow a
            new product company.
          </p>
          <ul>
            <li>Future Development Roadmap</li>
            <li>24/7 Support Infrastructure</li>
            <li>Analyze Product Usage</li>
            <li>Joint Ventures</li>
            <li>Incubator for Productization Partnerships</li>
          </ul>
        </ServiceContainer>
        <ServiceContainer>
          <Image
            staticGatsbyImage={
              <StaticImage
                src="../images/services/partnership.png"
                alt="A Launchcode meeting with one of our partners"
                loading="lazy"
                layout="fullWidth"
                placeholder="dominantColor"
                quality={100}
                formats={['auto']}
              />
            }
            disableAnimation
          />
        </ServiceContainer>
      </ServiceSectionContentFlipped>
    </ServiceSection>
    <ExtendedBg backgroundColor="#f7f7f9">
      <ServiceBottomSectionContent>
        <SpacingWrapper>
          <PaddedDiv>
            <ResponsiveDiv>
              <ServiceBottomSectionHeader>
                <h2>Benefits of working with Launchcode</h2>
              </ServiceBottomSectionHeader>
              <ServiceBottomSectionGrid>
                <ServiceBottomSectionContainer>
                  <h3>Working with startups</h3>
                  <p>
                    We understand the limited resources of startups, we've been there before. We'll
                    help you along the way by creating proof of concepts that you can use to gain
                    interest from your audience and investor. We can also partner up with you and
                    leverage our whole development, support and back office infrastructure to get
                    you up and running quickly.
                  </p>
                </ServiceBottomSectionContainer>
                <ServiceBottomSectionContainer>
                  <h3>We embrace cool technologies</h3>
                  <p>
                    We are a team of diverse developers with experience in building many different
                    technologies such as iOS & Android apps, Web apps, Artificial Intelligence, and
                    Data warehouses, Data visualizations. We're always learning the new tech trends,
                    so when we work together, we will use the best tech for the product.
                  </p>
                </ServiceBottomSectionContainer>
                <ServiceBottomSectionContainer>
                  <h3>We love hardware too</h3>
                  <p>
                    We love to geek out in our tech lab with 3D printers, CNC machines, CAD design,
                    IoT devices, robotics and virtual reality. No task is too big or too small, we
                    can rapid prototype your solution and use our manufacturing partners to scale up
                    production when you release your product.
                  </p>
                </ServiceBottomSectionContainer>
                <ServiceBottomSectionContainer>
                  <h3>We build things that don't exist</h3>
                  <p>
                    Most of our projects are for products that don't exist. We love creating new and
                    innovative solutions.
                  </p>
                </ServiceBottomSectionContainer>
                <ServiceBottomSectionContainer>
                  <h3>Flexible Payment</h3>
                  <p>
                    We like to be accountable, so we only charge for work that we've completed so
                    that you're happy with the results.
                  </p>
                </ServiceBottomSectionContainer>
              </ServiceBottomSectionGrid>
            </ResponsiveDiv>
          </PaddedDiv>
        </SpacingWrapper>
      </ServiceBottomSectionContent>
    </ExtendedBg>
  </>
);

const ServiceSection = styled(Section)`
  padding: 1rem 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 2rem 0;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 4rem 0;
  }

  :last-of-type {
    margin-bottom: 3rem;

    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      margin-bottom: 0;
    }
  }
`;

const ServiceSectionContent = styled(SectionContent)`
  flex-direction: column;
  justify-content: space-between;
  align-items: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: row;
  }

  > div {
    padding: 1rem 0;

    @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
      padding: 2rem 0;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      padding: 4rem 0;
    }
  }
`;

const ServiceSectionContentFlipped = styled(ServiceSectionContent)`
  flex-direction: column-reverse;
`;

const ServiceContainer = styled(Section)`
  flex: 1;
  align-items: flex-start;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-right: 6rem;
  }

  :last-of-type {
    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      margin-right: 0;
    }
  }

  ul {
    margin: 0;
    list-style-type: none;

    > li {
      position: relative;
      padding-left: 0.85rem;
      font-size: 1.25rem;

      :before {
        position: absolute;
        left: 0;
        content: '-';
      }
    }
  }
`;

const ServiceBottomSectionContent = styled(CenteredContent)`
  flex-direction: column;
`;

const ServiceBottomSectionHeader = styled(CenteredContent)`
  margin-bottom: 2rem;

  h2 {
    color: #1d1929;
    font-size: 2.125rem;
    font-weight: 900;
    text-align: center;
    width: 20ch;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-bottom: 5rem;

    h2 {
      font-size: 3.25rem;
      padding: 0;
    }
  }
`;

const ServiceBottomSectionGrid = styled(SectionContent)`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  row-gap: 3rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 2fr);
    column-gap: 5rem;
    row-gap: 5rem;
  }
`;

const ServiceBottomSectionContainer = styled(SectionContent)`
  width: 100%;

  h3 {
    font-size: 1.75rem;
    color: #1d1929;
    font-weight: 900;
  }
`;

export default ServicesPage;
